import React, { useCallback, useEffect, useMemo, useState } from 'react';
import MasterLayout from '../../masterLayout/MasterLayout';
import Breadcrumb from '../../components/Breadcrumb';
import StickyTableResponsive from '../../components/common/StickyTableResponsive';
import { notify } from '../../components/common/AppToast';
import {
  AppAlert,
  AppBadge,
  AppButton,
  AppCheckbox,
  AppConfirmModal,
  AppFormField,
  AppInput,
  AppModal,
  AppPagination,
  AppSelect,
  AppSpinner,
  AppTabs,
  AppTextarea,
} from '../../components/ui';
import UserRolesPicker, { UserRoleBadge } from '../../components/admin/UserRolesPicker';
import useUserRoles from '../../hooks/useUserRoles';
import skillsService from '../../services/skillsService';
import mcpDomainsService from '../../services/mcpDomainsService';
import depotService from '../../services/depotService';
import '../../styles/kkp-planning-page.scss';
import '../../styles/skills-admin.scss';

const emptyParam = () => ({ nom: '', type: 'texte', label: '', requis: false });

const emptyForm = () => ({
  slug: '',
  nom: '',
  description: '',
  mode: 'webhook',
  webhook_url: '',
  sql_requete: '',
  actif: true,
  roles_autorises: [],
  domaine: '',
  parametres: [emptyParam()],
});

const STATUT_BADGE = {
  en_cours: { variant: 'info', label: 'En cours' },
  lance: { variant: 'warning', label: 'Lancé' },
  termine: { variant: 'success', label: 'Terminé' },
  erreur: { variant: 'danger', label: 'Erreur' },
};

const PARAM_TYPES = [
  { value: 'client', label: 'Client' },
  { value: 'periode', label: 'Période' },
  { value: 'texte', label: 'Texte' },
];

function skillToForm(skill) {
  return {
    slug: skill.slug || '',
    nom: skill.nom || '',
    description: skill.description || '',
    mode: skill.mode || 'webhook',
    webhook_url: skill.webhook_url || '',
    sql_requete: skill.sql_requete || '',
    actif: skill.actif !== false,
    roles_autorises: Array.isArray(skill.roles_autorises) ? [...skill.roles_autorises] : [],
    domaine: skill.domaine || '',
    parametres: Array.isArray(skill.parametres) && skill.parametres.length > 0
      ? skill.parametres.map((param) => ({
          nom: param.nom || '',
          type: param.type || 'texte',
          label: param.label || '',
          requis: Boolean(param.requis),
        }))
      : [emptyParam()],
  };
}

function formatDate(value) {
  if (!value) {
    return '—';
  }
  try {
    return new Date(value).toLocaleString('fr-FR');
  } catch {
    return value;
  }
}

export default function SkillsAdminPage() {
  const { roles: availableRoles } = useUserRoles();
  const [tab, setTab] = useState('skills');
  const [skills, setSkills] = useState([]);
  const [mcpDomains, setMcpDomains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [testSkill, setTestSkill] = useState(null);
  const [testParams, setTestParams] = useState({});
  const [testRunning, setTestRunning] = useState(false);
  const [testExecution, setTestExecution] = useState(null);
  const [driveClients, setDriveClients] = useState([]);
  const [executions, setExecutions] = useState([]);
  const [executionsMeta, setExecutionsMeta] = useState({ page: 1, total: 0, pages: 1 });
  const [journalFilters, setJournalFilters] = useState({ slug: '', statut: '', q: '' });

  const loadSkills = useCallback(async () => {
    setLoading(true);
    try {
      const [list, domainsData] = await Promise.all([
        skillsService.adminList(),
        mcpDomainsService.getAdminMatrix().catch(() => ({ catalog: [] })),
      ]);
      setSkills(list);
      setMcpDomains(domainsData.catalog || []);
    } catch (error) {
      notify.error(error.message || 'Impossible de charger les skills.');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadExecutions = useCallback(async (page = 1) => {
    try {
      const data = await skillsService.adminExecutions({
        page,
        limit: 30,
        ...journalFilters,
      });
      setExecutions(data.executions || []);
      setExecutionsMeta({
        page: data.pagination?.page || page,
        total: data.pagination?.total || 0,
        pages: data.pagination?.pages || 1,
      });
    } catch (error) {
      notify.error(error.message || 'Impossible de charger le journal.');
    }
  }, [journalFilters]);

  useEffect(() => {
    loadSkills();
  }, [loadSkills]);

  useEffect(() => {
    if (tab === 'journal') {
      loadExecutions(1);
    }
  }, [tab, loadExecutions]);

  const hasPending = useMemo(
    () => executions.some((item) => item.statut === 'en_cours' || item.statut === 'lance')
      || (testExecution && (testExecution.statut === 'en_cours' || testExecution.statut === 'lance')),
    [executions, testExecution],
  );

  useEffect(() => {
    if (!hasPending) {
      return undefined;
    }
    const timer = window.setInterval(() => {
      if (tab === 'journal') {
        loadExecutions(executionsMeta.page);
      }
      if (testExecution?.id && (testExecution.statut === 'en_cours' || testExecution.statut === 'lance')) {
        skillsService.getExecution(testExecution.id)
          .then(setTestExecution)
          .catch(() => {});
      }
    }, skillsService.POLL_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [hasPending, tab, loadExecutions, executionsMeta.page, testExecution]);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm());
    setModalOpen(true);
  };

  const openEdit = (skill) => {
    setEditingId(skill.id);
    setForm(skillToForm(skill));
    setModalOpen(true);
  };

  const updateParam = (index, patch) => {
    setForm((prev) => ({
      ...prev,
      parametres: prev.parametres.map((param, i) => (i === index ? { ...param, ...patch } : param)),
    }));
  };

  const toggleRole = (role) => {
    setForm((prev) => {
      const current = prev.roles_autorises || [];
      const next = current.includes(role)
        ? current.filter((item) => item !== role)
        : [...current, role];
      return { ...prev, roles_autorises: next };
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const body = {
        ...form,
        parametres: form.parametres.filter((param) => param.nom.trim() !== ''),
      };
      if (editingId) {
        await skillsService.adminUpdate(editingId, body);
        notify.success('Skill mis à jour.');
      } else {
        await skillsService.adminCreate(body);
        notify.success('Skill créé.');
      }
      setModalOpen(false);
      await loadSkills();
    } catch (error) {
      notify.error(error.message || 'Enregistrement impossible.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) {
      return;
    }
    try {
      await skillsService.adminRemove(deleteTarget.id);
      notify.success('Skill supprimé.');
      setDeleteTarget(null);
      await loadSkills();
    } catch (error) {
      notify.error(error.message || 'Suppression impossible.');
    }
  };

  const openTester = async (skill) => {
    setTestSkill(skill);
    setTestExecution(null);
    const initial = {};
    (skill.parametres || []).forEach((param) => {
      initial[param.nom] = '';
    });
    setTestParams(initial);
    try {
      const data = await depotService.listClientsDrive();
      setDriveClients(data.clients);
    } catch {
      setDriveClients([]);
    }
  };

  const handleTest = async () => {
    if (!testSkill) {
      return;
    }
    setTestRunning(true);
    try {
      const execution = await skillsService.adminTester(testSkill.id, testParams);
      setTestExecution(execution);
      if (execution.statut === 'termine') {
        notify.success('Skill terminé.');
      } else if (execution.statut === 'erreur') {
        notify.error(execution.message || 'Le skill a échoué.');
      } else {
        notify.info('Skill lancé, en attente du résultat…');
      }
      if (tab === 'journal') {
        loadExecutions(1);
      }
    } catch (error) {
      notify.error(error.message || 'Test impossible.');
    } finally {
      setTestRunning(false);
    }
  };

  return (
    <MasterLayout>
      <Breadcrumb
        title="Skills"
        subtitle="Registre des skills n8n / SQL, tests et journal d’exécution"
        actions={(
          <AppButton icon="mdi:plus" onClick={openCreate}>
            Nouveau skill
          </AppButton>
        )}
      />

      <AppTabs
        className="mb-16"
        value={tab}
        onChange={setTab}
        items={[
          { value: 'skills', label: 'Registre', icon: 'mdi:puzzle-outline' },
          { value: 'journal', label: 'Journal', icon: 'mdi:text-box-outline' },
        ]}
      />

      {tab === 'skills' && (
        <div className="card kkp-planning-results-card border-0 shadow-sm">
          {loading ? (
            <div className="p-24 text-center"><AppSpinner /></div>
          ) : (
            <StickyTableResponsive>
              <table className="table table-hover mb-0">
                <thead>
                  <tr>
                    <th>Skill</th>
                    <th>Mode</th>
                    <th>Paramètres</th>
                    <th>Rôles</th>
                    <th>Domaine</th>
                    <th>État</th>
                    <th className="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {skills.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="text-center text-secondary-light py-24">
                        Aucun skill. Créez-en un ou lancez <code>php bin/console app:skills:seed</code>.
                      </td>
                    </tr>
                  ) : skills.map((skill) => (
                    <tr key={skill.id}>
                      <td>
                        <div className="fw-semibold">/{skill.slug}</div>
                        <div className="text-sm">{skill.nom}</div>
                        <div className="text-secondary-light text-xs skills-admin__desc">{skill.description}</div>
                      </td>
                      <td>
                        <AppBadge variant={skill.mode === 'sql' ? 'info' : 'warning'}>
                          {skill.mode === 'sql' ? 'SQL' : 'n8n'}
                        </AppBadge>
                      </td>
                      <td className="text-sm">
                        {(skill.parametres || []).map((param) => param.nom).join(', ') || '—'}
                      </td>
                      <td>
                        {(skill.roles_autorises || []).length === 0 ? (
                          <span className="text-secondary-light text-sm">Tous</span>
                        ) : (
                          <div className="d-flex flex-wrap gap-4">
                            {skill.roles_autorises.map((role) => (
                              <UserRoleBadge key={role} role={role} />
                            ))}
                          </div>
                        )}
                      </td>
                      <td className="text-sm">
                        {skill.domaine || <span className="text-secondary-light">—</span>}
                      </td>
                      <td>
                        <AppBadge variant={skill.actif ? 'success' : 'neutral'}>
                          {skill.actif ? 'Actif' : 'Inactif'}
                        </AppBadge>
                      </td>
                      <td className="text-end text-nowrap">
                        <AppButton size="sm" variant="outline-primary" icon="mdi:play" onClick={() => openTester(skill)}>
                          Tester
                        </AppButton>
                        {' '}
                        <AppButton size="sm" variant="outline-secondary" icon="mdi:pencil" onClick={() => openEdit(skill)}>
                          Modifier
                        </AppButton>
                        {' '}
                        <AppButton size="sm" variant="outline-danger" icon="mdi:delete" onClick={() => setDeleteTarget(skill)}>
                          Supprimer
                        </AppButton>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </StickyTableResponsive>
          )}
        </div>
      )}

      {tab === 'journal' && (
        <div className="card kkp-planning-results-card border-0 shadow-sm">
          <div className="p-16 d-flex flex-wrap gap-8 align-items-end">
            <AppFormField label="Skill" className="mb-0">
              <AppSelect
                value={journalFilters.slug}
                onChange={(event) => setJournalFilters((prev) => ({ ...prev, slug: event.target.value }))}
              >
                <option value="">Tous</option>
                {skills.map((skill) => (
                  <option key={skill.slug} value={skill.slug}>/{skill.slug}</option>
                ))}
              </AppSelect>
            </AppFormField>
            <AppFormField label="Statut" className="mb-0">
              <AppSelect
                value={journalFilters.statut}
                onChange={(event) => setJournalFilters((prev) => ({ ...prev, statut: event.target.value }))}
              >
                <option value="">Tous</option>
                <option value="en_cours">En cours</option>
                <option value="lance">Lancé</option>
                <option value="termine">Terminé</option>
                <option value="erreur">Erreur</option>
              </AppSelect>
            </AppFormField>
            <AppFormField label="Recherche" className="mb-0">
              <AppInput
                value={journalFilters.q}
                onChange={(event) => setJournalFilters((prev) => ({ ...prev, q: event.target.value }))}
                placeholder="Nom, résultat…"
              />
            </AppFormField>
            <AppButton variant="outline-secondary" onClick={() => loadExecutions(1)}>
              Filtrer
            </AppButton>
          </div>
          <StickyTableResponsive>
            <table className="table table-hover mb-0">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Skill</th>
                  <th>Utilisateur</th>
                  <th>Statut</th>
                  <th>Résultat</th>
                </tr>
              </thead>
              <tbody>
                {executions.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="text-center text-secondary-light py-24">Aucune exécution.</td>
                  </tr>
                ) : executions.map((execution) => {
                  const badge = STATUT_BADGE[execution.statut] || STATUT_BADGE.en_cours;
                  return (
                    <tr key={execution.id}>
                      <td className="text-nowrap text-sm">{formatDate(execution.created_at)}</td>
                      <td>
                        <div className="fw-semibold">/{execution.skill_slug}</div>
                        <div className="text-xs text-secondary-light">{execution.skill_nom}</div>
                      </td>
                      <td className="text-sm">{execution.utilisateur?.nom || execution.utilisateur?.email || '—'}</td>
                      <td><AppBadge variant={badge.variant}>{badge.label}</AppBadge></td>
                      <td className="text-sm skills-admin__result">
                        {execution.statut === 'erreur' ? execution.message : execution.resultat}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </StickyTableResponsive>
          {executionsMeta.pages > 1 && (
            <div className="p-12">
              <AppPagination
                variant="simple"
                page={executionsMeta.page}
                pages={executionsMeta.pages}
                total={executionsMeta.total}
                totalLabel={`${executionsMeta.total} exécution${executionsMeta.total > 1 ? 's' : ''}`}
                onPageChange={loadExecutions}
              />
            </div>
          )}
        </div>
      )}

      <AppModal
        show={modalOpen}
        title={editingId ? 'Modifier le skill' : 'Nouveau skill'}
        size="lg"
        scrollable
        onClose={() => setModalOpen(false)}
        footer={(
          <>
            <AppButton variant="outline-secondary" onClick={() => setModalOpen(false)} disabled={saving}>
              Annuler
            </AppButton>
            <AppButton loading={saving} onClick={handleSave}>
              Enregistrer
            </AppButton>
          </>
        )}
      >
        <div className="row g-3">
          <div className="col-md-4">
            <AppFormField label="Slug" required hint="Commande du menu /">
              <AppInput
                value={form.slug}
                onChange={(event) => setForm((prev) => ({ ...prev, slug: event.target.value }))}
                placeholder="contexte"
              />
            </AppFormField>
          </div>
          <div className="col-md-8">
            <AppFormField label="Nom" required>
              <AppInput
                value={form.nom}
                onChange={(event) => setForm((prev) => ({ ...prev, nom: event.target.value }))}
              />
            </AppFormField>
          </div>
          <div className="col-12">
            <AppFormField
              label="Description"
              required
              hint="Lue dans le menu / et par l’IA : ce que fait le skill, quand l’utiliser, quand ne pas l’utiliser."
            >
              <AppTextarea
                rows={4}
                value={form.description}
                onChange={(event) => setForm((prev) => ({ ...prev, description: event.target.value }))}
              />
            </AppFormField>
          </div>
          <div className="col-md-4">
            <AppFormField label="Mode">
              <AppSelect
                value={form.mode}
                onChange={(event) => setForm((prev) => ({ ...prev, mode: event.target.value }))}
              >
                <option value="webhook">Webhook n8n</option>
                <option value="sql">SQL lecture seule</option>
              </AppSelect>
            </AppFormField>
          </div>
          <div className="col-md-8 d-flex align-items-end">
            <AppCheckbox
              id="skill-actif"
              label="Actif (visible dans le menu /)"
              checked={form.actif}
              onChange={(event) => setForm((prev) => ({ ...prev, actif: event.target.checked }))}
            />
          </div>
          <div className="col-12">
            <AppFormField
              label="Rôles autorisés"
              hint="Aucun rôle coché = accessible à tous les utilisateurs connectés. Sinon, au moins un des rôles (hiérarchie Symfony respectée)."
            >
              <UserRolesPicker
                roles={availableRoles}
                selected={form.roles_autorises}
                onToggle={toggleRole}
                disabled={saving}
              />
            </AppFormField>
          </div>
          <div className="col-12">
            <AppFormField
              label="Domaine MCP"
              hint="Optionnel. Si renseigné, l’utilisateur doit aussi avoir ce domaine dans la matrice Domaines MCP."
            >
              <AppSelect
                value={form.domaine}
                onChange={(event) => setForm((prev) => ({ ...prev, domaine: event.target.value }))}
              >
                <option value="">Aucun (rôles seuls)</option>
                {mcpDomains.map((domain) => (
                  <option key={domain.key} value={domain.key}>
                    {domain.label} ({domain.key})
                  </option>
                ))}
              </AppSelect>
            </AppFormField>
          </div>
          {form.mode === 'webhook' ? (
            <div className="col-12">
              <AppFormField label="URL webhook n8n" required>
                <AppInput
                  value={form.webhook_url}
                  onChange={(event) => setForm((prev) => ({ ...prev, webhook_url: event.target.value }))}
                  placeholder="https://n8n.iti-conseil.com/webhook/…"
                />
              </AppFormField>
            </div>
          ) : (
            <div className="col-12">
              <AppFormField label="Requête SQL (SELECT uniquement)" required hint="Paramètres : :client_nom, :client_slug, :periode…">
                <AppTextarea
                  rows={6}
                  value={form.sql_requete}
                  onChange={(event) => setForm((prev) => ({ ...prev, sql_requete: event.target.value }))}
                />
              </AppFormField>
            </div>
          )}
          <div className="col-12">
            <div className="d-flex justify-content-between align-items-center mb-8">
              <strong>Paramètres</strong>
              <AppButton
                size="sm"
                variant="outline-secondary"
                icon="mdi:plus"
                onClick={() => setForm((prev) => ({ ...prev, parametres: [...prev.parametres, emptyParam()] }))}
              >
                Ajouter
              </AppButton>
            </div>
            {form.parametres.map((param, index) => (
              <div key={index} className="row g-2 align-items-end mb-8">
                <div className="col-md-3">
                  <AppFormField label="Nom" className="mb-0">
                    <AppInput value={param.nom} onChange={(event) => updateParam(index, { nom: event.target.value })} />
                  </AppFormField>
                </div>
                <div className="col-md-3">
                  <AppFormField label="Type" className="mb-0">
                    <AppSelect value={param.type} onChange={(event) => updateParam(index, { type: event.target.value })}>
                      {PARAM_TYPES.map((type) => (
                        <option key={type.value} value={type.value}>{type.label}</option>
                      ))}
                    </AppSelect>
                  </AppFormField>
                </div>
                <div className="col-md-3">
                  <AppFormField label="Libellé" className="mb-0">
                    <AppInput value={param.label} onChange={(event) => updateParam(index, { label: event.target.value })} />
                  </AppFormField>
                </div>
                <div className="col-md-2">
                  <AppCheckbox
                    id={`param-requis-${index}`}
                    label="Requis"
                    checked={param.requis}
                    onChange={(event) => updateParam(index, { requis: event.target.checked })}
                  />
                </div>
                <div className="col-md-1">
                  <AppButton
                    size="sm"
                    variant="outline-danger"
                    icon="mdi:close"
                    onClick={() => setForm((prev) => ({
                      ...prev,
                      parametres: prev.parametres.filter((_, i) => i !== index),
                    }))}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </AppModal>

      <AppModal
        show={Boolean(testSkill)}
        title={testSkill ? `Tester /${testSkill.slug}` : 'Tester'}
        onClose={() => { setTestSkill(null); setTestExecution(null); }}
        footer={(
          <>
            <AppButton variant="outline-secondary" onClick={() => { setTestSkill(null); setTestExecution(null); }}>
              Fermer
            </AppButton>
            <AppButton icon="mdi:play" loading={testRunning} onClick={handleTest}>
              Lancer
            </AppButton>
          </>
        )}
      >
        {(testSkill?.parametres || []).map((param) => (
          <AppFormField key={param.nom} label={param.label || param.nom} required={param.requis}>
            {param.type === 'client' ? (
              <AppSelect
                value={testParams[param.nom] || ''}
                onChange={(event) => setTestParams((prev) => ({ ...prev, [param.nom]: event.target.value }))}
              >
                <option value="">Choisir un client</option>
                {driveClients.map((client) => (
                  <option key={client.folder_id} value={client.slug}>
                    {client.nom}
                  </option>
                ))}
              </AppSelect>
            ) : (
              <AppInput
                value={testParams[param.nom] || ''}
                onChange={(event) => setTestParams((prev) => ({ ...prev, [param.nom]: event.target.value }))}
                placeholder={param.type === 'periode' ? 'juillet 2026' : ''}
              />
            )}
          </AppFormField>
        ))}
        {testExecution && (
          <AppAlert variant="secondary" className="mb-0">
            <div className="mb-8">
              <AppBadge variant={(STATUT_BADGE[testExecution.statut] || STATUT_BADGE.en_cours).variant}>
                {(STATUT_BADGE[testExecution.statut] || STATUT_BADGE.en_cours).label}
              </AppBadge>
            </div>
            <pre className="skills-admin__pre mb-0">
              {testExecution.statut === 'erreur' ? testExecution.message : (testExecution.resultat || 'En attente…')}
            </pre>
          </AppAlert>
        )}
      </AppModal>

      <AppConfirmModal
        show={Boolean(deleteTarget)}
        title="Supprimer ce skill ?"
        confirmLabel="Supprimer"
        confirmVariant="danger"
        onConfirm={handleDelete}
        onClose={() => setDeleteTarget(null)}
      >
        {deleteTarget ? (
          <p className="mb-0">
            Le skill <strong>/{deleteTarget.slug}</strong> sera retiré du registre. Le journal d’exécutions est conservé.
          </p>
        ) : null}
      </AppConfirmModal>
    </MasterLayout>
  );
}
